import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { showToast } from '@klpkonsern/ui';

import { useCurrentUser } from '@klp/auth';
import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import type { GodkjenningProsessResponse } from '@klp/prosjekt/interfaces';
import { useAssignProsjektProcessMutation } from '@klp/prosjekt/services/rtkApi';
import type { AssignProsjektProcessRequest } from '@klp/prosjekt/services/types';

export const useAssignResponsibleUser = (
    prosjektId: GodkjenningProsessResponse['prosjektId'],
    prosessInstansId: GodkjenningProsessResponse['prosessInstansId'],
    attachComment?: (comment: string) => string,
) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { currentUser } = useCurrentUser();
    const [assignTask, { isLoading: assigningTask }] =
        useAssignProsjektProcessMutation();

    const handlePrepareAssignResponsibleUserRequest = (
        ansvarlig: string,
        comment: string | undefined,
        endreProsessAnsvarlig = true,
    ) => {
        try {
            const request: AssignProsjektProcessRequest = {
                prosjektId,
                ansvarlig,
                formFields:
                    attachComment && comment
                        ? [
                              {
                                  id: CamundaFields.TILDEL_KOMMENTARER,
                                  value: attachComment(comment),
                                  type: 'string',
                                  validation: [],
                              },
                          ]
                        : [],
                prosessInstansId,
                endreProsessAnsvarlig,
            };
            return request;
        } catch (error) {
            showToast({ body: error.message, type: 'error' });
        }
        return;
    };

    const handleAssignResponsibleUser = async (
        ansvarlig: string,
        comment: string | undefined,
        redirectTo?: string,
        endreProsessAnsvarlig = true,
    ) => {
        if (!currentUser) return;

        const request = handlePrepareAssignResponsibleUserRequest(
            ansvarlig,
            comment,
            endreProsessAnsvarlig,
        );
        if (!request) return;

        await assignTask(request)
            .unwrap()
            .then(() => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });
                redirectTo && history.push(redirectTo);
            })
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };
    return { handleAssignResponsibleUser, assigningTask };
};
